import type { Event } from '../types';
import { PARTNERS, providerByName, usableOffer } from '../../shared/providers.mjs';
import { rememberOut } from '../lib/handoff';

/** The event's exact link on a provider's site (its field in shared/providers.mjs), if the build matched one. */
export const exactLink = (name: string, event: Event): string | undefined => {
  const p = providerByName(name);
  return p ? ((event as unknown as Record<string, string | undefined>)[p.field] ?? undefined) : undefined;
};

/** How many partner marketplaces list this exact event. */
export const partnerLinkCount = (event: Event) => PARTNERS.filter((p) => exactLink(p.name, event)).length;

export interface Marketplace {
  name: string;
  /** URL slug for the /go/ click router (logs the click, adds partner tracking, 302s on). */
  slug: string;
  kind: string;
  /** Honest fee posture — the real decision dimension while prices aren't live. */
  feeNote: string;
  guarantee: string;
  searchUrl: (q: string) => string;
  /** Commission partner — shown in a clearly labeled featured position. */
  partner?: boolean;
  /** True when this partner's feed prices are all-in (fees included). False =
   *  sticker price, fees added at checkout. Never mix these up in the UI. */
  allIn?: boolean;
  /** How the partner's page takes the party size, so the /out/ card can say what comes next:
   *  'preset' = our link carries it (goHref ends HelloTickets URLs in /<qty>), 'asks' = the
   *  visitor picks it again on arrival (Ticket Squeeze and TicketNetwork links carry none). */
  quantity?: 'preset' | 'asks';
}

/** Our first commission partner. Rendered as a labeled featured row, and only
 *  when the build matched this exact event (no generic fallback link exists). */
export const HELLOTICKETS: Marketplace = {
  name: 'HelloTickets',
  slug: 'hellotickets',
  kind: 'Resale marketplace',
  // Verified 2026-07-14: US storefront prices are ALL-IN (page total == API price).
  feeNote: 'Fees included — the price shown is what you pay',
  guarantee: 'Buyer guarantee',
  searchUrl: () => 'https://www.hellotickets.com/',
  partner: true,
  allIn: true,
  quantity: 'preset',
};

/** Commission partner #2 — affiliate datafeed gives exact event links + real
 *  per-event floor prices. Row appears only on a build-time exact match.
 *  Feed prices are STICKER prices (user-verified 2026-07-15: feed $56.35 vs
 *  $68 fees-included on their page) — always label "+ fees".
 *
 *  ^^ THIS MAY NO LONGER BE TRUE — DO NOT TRUST THE LABEL UNTIL RECHECKED.
 *  On 2026-08-23 ticketsqueeze.com showed "NO SERVICE FEES" in the header and
 *  "Fees Included" on every listing, and our feed price matched the displayed
 *  price to the dollar on both events tested:
 *    7847021 Daniel Caesar  feed $103  page $103 "Fees Included"
 *    7896603 Karol G        feed $120  page $120 "Fees Included"
 *  If that holds at checkout, `allIn` below is wrong and the site is telling
 *  shoppers fees get added when they do not — understating our own partner.
 *  Flag left UNCHANGED on purpose: the July finding came from a real checkout,
 *  this one only from the listing page, and a listing-page label is a claim,
 *  not proof. Resolve with ONE real checkout, then set the flag from that. */
export const TICKETSQUEEZE: Marketplace = {
  name: 'Ticket Squeeze',
  slug: 'ticketsqueeze',
  kind: 'Resale marketplace',
  feeNote: 'Fees added at checkout',
  guarantee: 'Buyer guarantee',
  searchUrl: () => 'https://www.ticketsqueeze.com/',
  partner: true,
  allIn: false,
  quantity: 'asks',
};

/** Commission partner #3 — TicketNetwork direct, via Impact. 12.5% on sale,
 *  30-day referral window (contract confirmed 2026-08-23).
 *
 *  SAME EXCHANGE AS TICKET SQUEEZE (verified 2026-08-23). Ticket Squeeze is a
 *  TicketNetwork Direct white-label: the numeric event ids are shared, so
 *  ticketnetwork.com/tickets/<id>/ and ticketsqueeze.com/tickets/<id>/ resolve
 *  to the identical event. Checked 3/3 against our crosswalk:
 *    7847021 -> Daniel Caesar, Ball Arena Denver, 2026-08-26
 *    7896603 -> Karol G, Lumen Field Seattle, 2026-08-26
 *    7561739 -> Olivia Dean, Toyota Center Houston, 2026-08-25
 *
 *  PRICING — TicketNetwork is NOT the cheaper "source" storefront. It is the
 *  dearer one. Seat-for-seat on the identical listing, both labelled "Fees
 *  Included" (checked 2026-08-23):
 *
 *    Daniel Caesar / Ball Arena     TS      TN     TN premium
 *      Upper Balcony 330 Row 12     $103    $115   +11.7%
 *      Upper Balcony 306 Row 16     $115    $129   +12.2%
 *      ADT Club 236 Row 2           $117    $132   +12.8%
 *    Karol G / Lumen Field
 *      Upper 300s 309 Row TT        $120    $135   +12.5%
 *      Upper 300s 310 Row SS        $120    $135   +12.5%
 *      Upper 300s 312 Row QQ        $120    $135   +12.5%
 *      Upper 300s 332 Row SS        $120    $135   +12.5%
 *
 *  The premium tracks the 12.5% commission almost exactly (whole-dollar
 *  rounding accounts for the spread). Read that plainly: on this exchange the
 *  affiliate commission is added to the shopper's price, not taken out of the
 *  seller's margin.
 *
 *  CONSEQUENCE — never render both on the same price board, and never route a
 *  matched event here. TN's price carries no independent information: it is
 *  TS's price times ~1.125, so as a board row it is a guaranteed loser, and as
 *  a destination it means knowingly charging the shopper ~12.5% more for the
 *  same seat to earn ~4 points more commission. On a site whose whole premise
 *  is finding the cheapest price, that is the deception the FTC actually
 *  prosecutes, and it is what a journalist would lead with.
 *
 *  Where TN legitimately earns: events with NO Ticket Squeeze crosswalk entry,
 *  and search/browse surfaces where no priced comparison is being shown. There
 *  it is upside with nothing cheaper being hidden. That is how it is wired.
 *
 *  allIn: TRUE. ticketnetwork.com labels every listing "Fees Included" and the
 *  displayed price is the checkout price (observed on 7847021: lowest listing
 *  $115 ea, "Fees Included"). This is the OPPOSITE of the Ticket Squeeze feed,
 *  whose prices are sticker-only. Do not copy fee copy between the two.
 *  Verified on one event so far — re-check before this drives any UI text.
 *
 *  Links are the plain ticketnetwork.com/tickets/<id> event page. The Impact
 *  tracking link is built in the /go/ router for clicks its human gate trusts
 *  (functions/go/[market].ts, shared/go-gate.mjs), never in this bundle. */
export const TICKETNETWORK: Marketplace = {
  name: 'TicketNetwork',
  slug: 'ticketnetwork',
  kind: 'Resale marketplace',
  // TN prices we show are fees-included on TN's own storefront basis: the
  // Impact feed min matched the TN Seatics widget's cheapest group to the
  // cent (verified 2026-08-25; the earlier "sticker" reading was a
  // quantity-filter artifact — the feed min was a single "Last Seat" listing
  // a pair-filtered page hid). Offers carry allIn:true from the build either
  // way; this constant only covers a row with no offer at all.
  feeNote: 'Fees included — the price shown is what you pay',
  guarantee: 'Buyer guarantee',
  searchUrl: (q: string) => `https://www.ticketnetwork.com/search?q=${encodeURIComponent(q)}`,
  partner: true,
  allIn: true,
  quantity: 'asks',
};

/** Every partner marketplace a price board can show, alphabetical. Copy that
 *  names our partners reads them from here (PARTNER_NAMES, partnerList), so a
 *  partner joining or leaving is a one-line change, not a hunt through pages. */
export const PARTNER_MARKETPLACES: Marketplace[] = [HELLOTICKETS, TICKETSQUEEZE, TICKETNETWORK];
export const PARTNER_NAMES: string[] = PARTNER_MARKETPLACES.map((m) => m.name);

/** "A", "A and B", "A, B and C" — `and` is the word before the last item ("or" works too). */
export const listOf = (items: string[], and = 'and') =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;

/** "HelloTickets, Ticket Squeeze and TicketNetwork" */
export const partnerList = (and = 'and') => listOf(PARTNER_NAMES, and);

/** PARTNERS-ONLY FRONTEND (2026-07-15, user decision): event pages list ONLY
 *  monetized partners with an exact event match. Non-partner marketplaces
 *  (Ticketmaster/SeatGeek/StubHub/...) stay defined below for the day they
 *  approve us, but render nowhere. Publish gate guarantees every published
 *  event has at least one partner link. */
/** Switch for the Ticketmaster row — see the comment in marketplacesFor. */
export const TICKETMASTER_PARTNER = false;

export const marketplacesFor = (event: Event): Marketplace[] => [
  ...(event.tsUrl ? [TICKETSQUEEZE] : []),
  ...(event.htUrl ? [HELLOTICKETS] : []),
  // OWNER DECISION 2026-08-25: TN renders alongside TS even though they front
  // the same exchange (TN storefront prices ~11% above TS group-for-group).
  // The defense is that both rows carry each storefront's own widget-verified
  // fees-included price and the board sorts cheapest-first — the shopper sees
  // the real spread and decides, same as we will treat viagogo/StubHub if
  // they approve us. Shared warehouse, honest prices, informed choice.
  ...(event.tnUrl ? [TICKETNETWORK] : []),
  // Ticketmaster renders only once it pays (partners-only rule above). Its
  // Discovery key returns Impact-wrapped links, but the owner is NOT approved
  // in Ticketmaster's affiliate program (2026-10-03: Impact login answers with
  // a rejection), so a TM row would send buyers away from paying partners for
  // nothing. tmUrl stays in the catalog (always the PLAIN ticketmaster.com
  // page) so this is a one-line switch, together with TM_AFFILIATE_LIVE in
  // functions/go/[market].ts, the day a TM contract is live. The row is
  // unpriced ("see listings"): the public Discovery API carries no prices.
  ...(TICKETMASTER_PARTNER && event.tmUrl ? MARKETPLACES.filter((m) => m.name === 'Ticketmaster') : []),
];

/** This marketplace's own floor price for this event (today's feed), if it
 *  shared one with us. Non-partner sites never get an invented number. */
export const priceFor = (m: Marketplace, event: Event) =>
  event.offers?.find((o) => o.source === m.name) ?? null;

/** How many of the board's marketplaces give a price we stand behind (shared/providers.mjs usableOffer): a
 *  "see listings" link, a stand-in, a stale or a suspect price is a link, not a compared price (audit TIX-011). */
export const quotedCount = (event: Event) => marketplacesFor(event).filter((m) => usableOffer(priceFor(m, event))).length;

// Ordered by US brand recognition. Ranking is never sold; when live prices
// ship, priced rows sort cheapest-first above unpriced ones.
export const MARKETPLACES: Marketplace[] = [
  {
    name: 'Ticketmaster',
    slug: 'ticketmaster',
    kind: 'Primary — official',
    feeNote: 'Official primary tickets; fees added at checkout',
    guarantee: 'Verified Tickets',
    searchUrl: (q) => `https://www.ticketmaster.com/search?q=${encodeURIComponent(q)}`,
  },
  {
    name: 'StubHub',
    slug: 'stubhub',
    kind: 'Resale marketplace',
    feeNote: 'Fees added at checkout (often 25-30%)',
    guarantee: 'FanProtect guarantee',
    // /explore is StubHub's real discovery route (verified); their site search is POST-only.
    searchUrl: (q) => `https://www.stubhub.com/explore?q=${encodeURIComponent(q)}`,
  },
  {
    name: 'SeatGeek',
    slug: 'seatgeek',
    kind: 'Primary + resale',
    feeNote: 'Can display all-in prices',
    guarantee: 'Buyer Guarantee',
    searchUrl: (q) => `https://seatgeek.com/search?search=${encodeURIComponent(q)}`,
  },
  {
    name: 'TickPick',
    slug: 'tickpick',
    kind: 'Resale marketplace',
    feeNote: 'No buyer fees — price shown is final',
    guarantee: 'BuyerTrust guarantee',
    searchUrl: (q) => `https://www.tickpick.com/search?q=${encodeURIComponent(q)}`,
  },
  {
    name: 'Vivid Seats',
    slug: 'vividseats',
    kind: 'Resale marketplace',
    feeNote: 'Fees added at checkout',
    guarantee: '100% Buyer Guarantee',
    searchUrl: (q) => `https://www.vividseats.com/search?searchTerm=${encodeURIComponent(q)}`,
  },
  {
    name: 'Gametime',
    slug: 'gametime',
    kind: 'Resale marketplace',
    feeNote: 'All-in pricing toggle; strong last-minute deals',
    guarantee: 'Gametime Guarantee',
    searchUrl: (q) => `https://gametime.co/search?q=${encodeURIComponent(q)}`,
  },
];
// NOTE: viagogo removed for now — its site search is POST-only (no linkable
// results URL; old /search?searchText= dead-ends on an error page). Restore the
// row once the Partnerize affiliate approval provides real deep-link tooling.

/** Query string used to search marketplaces for a given event. */
export const marketplaceQuery = (e: {
  artist?: string;
  teams?: string[];
  name: string;
  city: string;
  tournament?: string;
  context?: string;
}) => {
  if (e.artist) return `${e.artist} ${e.city}`;
  const realTeams = e.teams && !e.teams.some((t) => /TBD|Final|Round|Match|Winner/i.test(t));
  if (realTeams) return e.teams!.join(' ');
  if (e.tournament) return `${e.tournament} ${e.context ?? ''}`.trim();
  return e.name;
};

/** Deep-link to the exact event page when the build matched one. */
export const marketplaceHref = (m: Marketplace, event: Event, query: string) => exactLink(m.name, event) ?? m.searchUrl(query);

/** Every outbound click lands on the /out/ interstitial (pages/Go.tsx — the
 *  "Taking you to…" card) which continues to the /go/ edge router: that logs
 *  the click (EPC data), appends partner tracking server-side, and 302s
 *  onward. HelloTickets URLs end in /<quantity> — qty preselects it on their
 *  page. `price` is the floor we showed; the card ignores it (a crafted link
 *  could carry any number) and repeats the board from the hand-off record
 *  instead: this remembers what each link stood for, and the click
 *  (trackOutbound) stores the one that was pressed (src/lib/handoff.ts). */
export const goHref = (m: Marketplace, event: Event, query: string, qty?: number, price?: number) => {
  let target = marketplaceHref(m, event, query);
  if (m.name === 'HelloTickets' && event.htUrl && qty && qty >= 1 && qty <= 20) {
    target = event.htUrl.replace(/\/\d+$/, `/${Math.floor(qty)}`);
  }
  rememberOut(m, event, qty);
  const priceParam = typeof price === 'number' && price > 0 ? `&price=${encodeURIComponent(String(price))}` : '';
  return `/out/${m.slug}?u=${encodeURIComponent(target)}&event=${encodeURIComponent(event.id)}${priceParam}`;
};

/** The marketplace behind a /go/ or /out/ slug, if we know it. */
export const marketplaceBySlug = (slug: string): Marketplace | null =>
  [HELLOTICKETS, TICKETSQUEEZE, TICKETNETWORK, ...MARKETPLACES].find((m) => m.slug === slug) ?? null;

/** Display name for a /go/ or /out/ slug. Unknown slugs get a neutral label —
 *  the edge router bounces them home anyway. */
export const marketplaceName = (slug: string) => marketplaceBySlug(slug)?.name ?? 'the marketplace';

/** True when this row links to the exact event, not search results. */
export const hasExactLink = (m: Marketplace, event: Event) => !!exactLink(m.name, event);

// Pared to the sentence the FTC actually requires: the commission, and that it
// costs the buyer nothing. The rest of the old paragraph ("independent
// comparison site", "every marketplace is an official partner", "prices are set
// by sellers") was self-description repeated three more times on the same page,
// and it sat between the shopper and the buy button.
//
// This line stays visible next to the affiliate links. It is not moved to the
// terms page: affiliate disclosure has to be clear and conspicuous where the
// links are, and we have told journalists in writing that it is on every page.
export const AFFILIATE_DISCLOSURE =
  'We may earn a commission when you buy through these links, at no extra cost to you.';
